import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';

import { GET_FAI_STATUS } from 'lib/api/queries';
import { getMachine } from 'lib/selectors/getMachine';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getFAIEnabled } from 'lib/selectors/getFAIEnabled';
import {
  actionSetFAIStatus,
  actionLoadFAIStatus,
  actionClearFAIStatus,
} from 'lib/actions';

export const useRequestFAIStatus = () => {
  const machine = useSelector(getMachine);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const faiEnabled = useSelector(getFAIEnabled);
  const dispatch = useDispatch();
  const machineRef = machine?.machineRef;

  const workOrderOperationRef = latestActivitySet?.workOrderOperationRef;
  const skip = !faiEnabled || !machineRef || !workOrderOperationRef;

  const prevSkipRef = useRef(true);
  const connectionKeyRef = useRef(null);
  const connectionKey = skip ? null : `${machineRef}:${workOrderOperationRef}`;

  useEffect(() => {
    const wasSkipping = prevSkipRef.current;
    prevSkipRef.current = skip;

    if (!skip) {
      const keyChanged = connectionKeyRef.current !== connectionKey;
      if (wasSkipping || keyChanged) {
        connectionKeyRef.current = connectionKey;
        dispatch(actionLoadFAIStatus());
      }
    } else {
      connectionKeyRef.current = null;
      if (!faiEnabled) {
        dispatch(actionClearFAIStatus());
      } else {
        dispatch(actionSetFAIStatus({ currentInspection: null }));
      }
    }
  }, [skip, faiEnabled, dispatch, connectionKey]);

  return useQuery(GET_FAI_STATUS, {
    variables: {
      machineRef,
      workOrderOperationRef,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip,
    onCompleted: (data) => {
      dispatch(
        actionSetFAIStatus({
          currentInspection: data?.faiStatus?.currentInspection ?? null,
        })
      );
    },
    onError: () => {
      dispatch(actionSetFAIStatus({ currentInspection: null }));
    },
  });
};
